import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Observable,
  defer,
  distinctUntilChanged,
  finalize,
  map,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly pendingRequests = new BehaviorSubject<number>(0);
  private readonly navigationIds = new Set<number>();
  readonly isLoading$ = this.pendingRequests.pipe(
    map((count: number) => count > 0),
    distinctUntilChanged(),
  );

  constructor() {
    inject(Router)
      .events.pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.navigationIds.add(event.id);
          this.pendingRequests.next(this.pendingRequests.value + 1);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError ||
          event instanceof NavigationSkipped
        ) {
          if (this.navigationIds.delete(event.id)) {
            this.pendingRequests.next(this.pendingRequests.value - 1);
          }
        }
      });
  }

  track<T>(source: Observable<T>): Observable<T> {
    return defer(() => {
      this.pendingRequests.next(this.pendingRequests.value + 1);
      return source.pipe(
        finalize(() => {
          this.pendingRequests.next(this.pendingRequests.value - 1);
        }),
      );
    });
  }
}

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private readonly loadingService = inject(LoadingService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.loadingService.track(next.handle(request));
  }
}
